/**
 * GoalsTable — DataTable + TableToolbar wrapper for the Goals half
 * of Step 7. FilterDrawer keyed on `security_property` multi-select.
 * Mirror of Step 4 / 5 / 6 tables.
 */
import React, { useEffect, useMemo, useState } from 'react';
import {
  DataTable,
  TableToolbar,
  TablePagination,
  RowActionsMenu,
} from '../../../../components/ui/DataTable';
import FilterDrawer from '../../../../components/ui/DataTable/FilterDrawer';
import { Icon } from '../../../../components/ui/Icon';
import { Button } from '../../../../components/ui/Button2';
import {
  GOAL_COLUMNS_READ,
  GOAL_COLUMNS_EDIT,
  GOAL_ACTIONS_COLUMN_WIDTH,
  SECURITY_PROPERTIES,
  CAL_LEVELS,
  type GoalRecord,
} from './goalsFieldSchema';
import styles from './Step7Tables.module.css';

const DataTableAny = DataTable as unknown as React.ComponentType<any>;
const TableToolbarAny = TableToolbar as unknown as React.ComponentType<any>;
const TablePaginationAny = TablePagination as unknown as React.ComponentType<any>;
const RowActionsMenuAny = RowActionsMenu as unknown as React.ComponentType<any>;
const FilterDrawerAny = FilterDrawer as unknown as React.ComponentType<any>;
// Button2 is a JSX forwardRef primitive; cast for use from TSX (mirrors the
// ButtonAny convention across the step-components).
const ButtonAny = Button as unknown as React.ComponentType<any>;

const DEFAULT_PAGE_SIZE = 20;

interface SortState {
  column: string;
  direction: 'asc' | 'desc';
}

export interface GoalsTableProps {
  goals: GoalRecord[];
  loading?: boolean;
  emptyMessage?: string;
  editable?: boolean;
  onAdd?: () => void;
  onEdit?: (goal: GoalRecord) => void;
  onDelete?: (goal: GoalRecord) => void;
  onExport?: () => void;
  onImport?: (file: File) => void;
}

function sortGoals(rows: GoalRecord[], sort: SortState | null): GoalRecord[] {
  if (!sort) return rows;
  const dir = sort.direction === 'asc' ? 1 : -1;
  return [...rows].sort((a, b) => {
    const av = (a as Record<string, unknown>)[sort.column];
    const bv = (b as Record<string, unknown>)[sort.column];
    if (av == null && bv == null) return 0;
    if (av == null) return 1;
    if (bv == null) return -1;
    return String(av).localeCompare(String(bv)) * dir;
  });
}

function searchGoals(rows: GoalRecord[], query: string): GoalRecord[] {
  const q = query.trim().toLowerCase();
  if (!q) return rows;
  return rows.filter((r) =>
    Object.values(r).some((v) =>
      v == null
        ? false
        : Array.isArray(v)
        ? v.join(' ').toLowerCase().includes(q)
        : String(v).toLowerCase().includes(q),
    ),
  );
}

export default function GoalsTable({
  goals,
  loading,
  emptyMessage = 'No cybersecurity goals yet.',
  editable = false,
  onAdd,
  onEdit,
  onDelete,
  onExport,
  onImport,
}: GoalsTableProps) {
  const fileInputRef = React.useRef<HTMLInputElement | null>(null);
  const [filterOpen, setFilterOpen] = useState(false);
  const [propertyValues, setPropertyValues] = useState<string[]>([]);
  const [calValues, setCalValues] = useState<string[]>([]);
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState<SortState | null>(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState<number>(DEFAULT_PAGE_SIZE);

  const propertyCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const p of SECURITY_PROPERTIES) counts[p.value] = 0;
    for (const g of goals) {
      const k = String(g.security_property ?? '');
      if (k in counts) counts[k] += 1;
    }
    return counts;
  }, [goals]);

  const calCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const c of CAL_LEVELS) counts[c.value] = 0;
    for (const g of goals) {
      const k = String(g.cal_level ?? '');
      if (k in counts) counts[k] += 1;
    }
    return counts;
  }, [goals]);

  const propertyFiltered = useMemo(
    () => (propertyValues.length === 0
      ? goals
      : goals.filter((g) => propertyValues.includes(String(g.security_property ?? '')))),
    [goals, propertyValues],
  );
  const calFiltered = useMemo(
    () => (calValues.length === 0
      ? propertyFiltered
      : propertyFiltered.filter((g) => calValues.includes(String(g.cal_level ?? '')))),
    [propertyFiltered, calValues],
  );
  const searched = useMemo(() => searchGoals(calFiltered, search), [calFiltered, search]);
  const sorted = useMemo(() => sortGoals(searched, sort), [searched, sort]);

  const totalPages = Math.max(1, Math.ceil(sorted.length / pageSize));
  useEffect(() => {
    if (page > totalPages) setPage(1);
  }, [page, totalPages]);

  const paged = useMemo(
    () => sorted.slice((page - 1) * pageSize, page * pageSize),
    [sorted, page, pageSize],
  );

  const keyed = useMemo(
    () =>
      paged.map((r, i) => ({
        ...r,
        _rowKey: `${r.goal_id ?? 'row'}::${(page - 1) * pageSize + i}`,
      })),
    [paged, page, pageSize],
  );

  const columns = useMemo(() => {
    if (!editable) return GOAL_COLUMNS_READ;
    return [
      ...GOAL_COLUMNS_EDIT,
      {
        key: '_actions',
        label: '',
        align: 'right',
        width: GOAL_ACTIONS_COLUMN_WIDTH,
        sortable: false,
        render: (row: GoalRecord) => (
          <RowActionsMenuAny
            ariaLabel={`Actions for ${row.goal_id}`}
            actions={[
              { id: 'edit', label: 'Edit', icon: <Icon name="edit" size={14} />, onClick: () => onEdit?.(row) },
              { id: 'delete', label: 'Delete', icon: <Icon name="delete" size={14} />, danger: true, onClick: () => onDelete?.(row) },
            ]}
          />
        ),
      },
    ];
  }, [editable, onEdit, onDelete]);

  const propertyFilter = {
    key: 'security_property',
    label: 'Property',
    values: propertyValues,
    onChange: (next: string[]) => {
      setPropertyValues(next);
      setPage(1);
    },
    options: SECURITY_PROPERTIES.map((p) => ({
      value: p.value,
      label: p.label,
      count: propertyCounts[p.value] ?? 0,
    })),
  };

  const calFilter = {
    key: 'cal_level',
    label: 'CAL',
    values: calValues,
    onChange: (next: string[]) => {
      setCalValues(next);
      setPage(1);
    },
    options: CAL_LEVELS.map((c) => ({
      value: c.value,
      label: c.label,
      count: calCounts[c.value] ?? 0,
    })),
  };

  const toolbarActions = (
    <div className={styles.toolbarActions}>
      {onExport && sorted.length > 0 && (
        <ButtonAny variant="outline" size="sm" onClick={onExport}>
          <Icon name="upload" size={14} /> Export CSV
        </ButtonAny>
      )}
      {editable && onImport && (
        <>
          <ButtonAny
            variant="outline"
            size="sm"
            onClick={() => fileInputRef.current?.click()}
          >
            <Icon name="download" size={14} /> Import
          </ButtonAny>
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv,.json"
            style={{ display: 'none' }}
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) onImport(f);
              if (e.target) e.target.value = '';
            }}
          />
        </>
      )}
      {editable && onAdd && (
        <ButtonAny variant="default" size="sm" onClick={onAdd}>
          <Icon name="add" size={14} /> Add Goal
        </ButtonAny>
      )}
    </div>
  );

  return (
    <div className={styles.tableShell}>
      <FilterDrawerAny
        isOpen={filterOpen}
        onClose={() => setFilterOpen(false)}
        filters={[propertyFilter, calFilter]}
      />
      <DataTableAny
        columns={columns}
        data={keyed}
        rowKey="_rowKey"
        sort={sort}
        onSortChange={(column: string | null, direction: 'asc' | 'desc' | null) =>
          setSort(column && direction ? { column, direction } : null)
        }
        loading={loading}
        emptyMessage={emptyMessage}
        toolbar={
          <TableToolbarAny
            search={{
              value: search,
              onChange: (v: string) => {
                setSearch(v);
                setPage(1);
              },
              placeholder: 'Search goals…',
            }}
            count={{ value: sorted.length, label: 'Goals' }}
            onFilterToggle={() => setFilterOpen((o) => !o)}
            filterButtonActive={filterOpen || propertyValues.length > 0 || calValues.length > 0}
            actions={toolbarActions}
          />
        }
        pagination={
          <TablePaginationAny
            currentPage={page}
            totalItems={sorted.length}
            itemsPerPage={pageSize}
            onPageChange={setPage}
            onItemsPerPageChange={(size: number) => {
              setPageSize(size);
              setPage(1);
            }}
          />
        }
      />
    </div>
  );
}
